import { isJsonObject, type JsonObject, type JsonValue } from '@/api/json';
import type { ToastMeta } from '@/queries/toastMeta';
import { createSentryPiniaPlugin } from '@sentry/vue';
import { createApp, markRaw } from 'vue';
import { createPinia } from 'pinia';
import { PiniaColada, PiniaColadaQueryHooksPlugin } from '@pinia/colada';
import { globalErrors } from '@/composables/useErrors';
import LaravelError, { toError } from '@/api/model/LaravelError';
import App from './App.vue';

// Browser-level fetch failures (aborted requests, navigation tear-down,
// server unreachable) all surface as either our LaravelError wrapper with a
// ERR_CANCELED/ERR_NETWORK code or as a raw TypeError with "Failed to fetch".
function isTransportError(error: Error): boolean {
    if (error instanceof LaravelError) {
        const code = error.localCause?.code;
        return code === 'ERR_CANCELED' || code === 'ERR_NETWORK';
    }
    return error instanceof TypeError && /Failed to fetch|NetworkError|Load failed/.test(error.message);
}

// You can use the following starter router instead of the default one as a clean starting point
// import router from "./router/starter";
import router from '@/router';

// Main Stylesheet
import '@/../scss/main.scss';

// Template components
import BaseBlock from '@/components/BaseBlock.vue';
import BaseBackground from '@/components/BaseBackground.vue';
import BasePageHeading from '@/components/BasePageHeading.vue';

// Template directives
import clickRipple from '@/directives/clickRipple';

import { configureEcho, echo } from '@laravel/echo-vue';
import { configureApiFetch } from '@/lib/http';
import { piniaColadaQueryDefaults } from '@/lib/piniaColadaDefaults';
import { useAuthStore } from '@/stores/authStore';

configureEcho({
    broadcaster: 'reverb',
});

// Craft new application
const app = createApp(App);

// Register global components
app.component('BaseBlock', BaseBlock);
app.component('BaseBackground', BaseBackground);
app.component('BasePageHeading', BasePageHeading);

// Register global directives
app.directive('click-ripple', clickRipple);

import Swal from 'sweetalert2';
window.Swal = Swal;

// Use Pinia and Vue Router
const pinia = createPinia();
pinia.use(createSentryPiniaPlugin());
pinia.use(({ store }) => {
    store.router = markRaw(router);
});
app.use(pinia);
app.use(PiniaColada, {
    queryOptions: piniaColadaQueryDefaults,
    plugins: [
        // Centralised toast for queries: any query that declares meta.errorMessage
        // gets a toast on failure. Mutations keep their own onSuccess/onError
        // because their messages are tightly coupled to specific user actions.
        PiniaColadaQueryHooksPlugin({
            onError(error, entry) {
                // SAFETY: ToastMeta is what this app attaches to a query's meta;
                // pinia-colada types it as an open record for everyone else's.
                const meta = entry.options?.meta as ToastMeta | undefined;
                if (!meta?.errorMessage) {
                    return;
                }
                // Skip toasting transport errors that fire while the page is
                // being torn down: a full navigation (page.goto in Playwright,
                // external link, reload) flips document.hidden to true and
                // then rejects every in-flight fetch as "Failed to fetch".
                // There is no one left to read a toast in that case, and the
                // browser's own error overlay already surfaces genuine
                // connectivity failures that happen while the tab is visible.
                if (document.hidden && isTransportError(toError(error))) {
                    return;
                }
                // Same idea for SPA-internal navigations: if pinia-colada has
                // already released every subscriber for this query by the
                // time the error fires, the data isn't going anywhere useful.
                if (entry.deps?.size === 0) {
                    return;
                }
                const errors = globalErrors();
                if (!errors) {
                    // setupGlobalErrors() hasn't run yet (e.g. a query failed before
                    // App.vue mounted GlobalErrorsInit). Swallow rather than throw
                    // so the original error isn't masked.
                    return;
                }
                errors.showErrors(toError(error), meta.errorMessage, meta.errorCategory);
            },
        }),
    ],
});
app.use(router);

let isHandlingUnauthorized = false;

configureApiFetch({
    getSocketId: () => {
        try {
            return echo().socketId();
        } catch {
            return null;
        }
    },
    async onUnauthorized(context) {
        if (context.url.includes('/api/users/me') || isHandlingUnauthorized) {
            return;
        }

        isHandlingUnauthorized = true;
        const authStore = useAuthStore();
        try {
            await authStore.logout();
            router.push({ name: 'login' });
        } finally {
            isHandlingUnauthorized = false;
        }
    },
    onCsrfMismatch() {
        const authStore = useAuthStore();
        authStore.setLoggedOut();
        router.push({ name: 'login' });
    },
});

// ..and finally mount it!
app.mount('#app');

import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';

import {
    ArcElement,
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js';
import 'chartjs-adapter-luxon';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
    Title,
    Tooltip,
    Legend,
    // Both controllers explicitly, because the Amazon Traffic chart mixes bars
    // and lines in one chart. vue-chartjs otherwise registers only the
    // controller its own component needs, leaving the mixed chart dependent on
    // some other view having imported <Line> first.
    BarController,
    LineController,
    BarElement,
    CategoryScale,
    LinearScale,
    ArcElement,
    LineElement,
    PointElement,
    TimeScale,
    Filler,
    annotationPlugin,
);

import * as Sentry from '@sentry/vue';

if (import.meta.env['VITE_SENTRY_DSN_PUBLIC']) {
    const sensitiveKeys =
        /password|secret|token|authorization|cookie|csrf|xsrf|api_key|private_key|session|bearer|credential/i;

    function stripSensitiveData(obj: JsonObject): JsonObject {
        const cleaned: JsonObject = {};
        for (const [key, value] of Object.entries(obj)) {
            if (sensitiveKeys.test(key)) {
                cleaned[key] = '[Filtered]';
            } else if (isJsonObject(value)) {
                cleaned[key] = stripSensitiveData(value);
            } else {
                cleaned[key] = value;
            }
        }
        return cleaned;
    }

    function stripSensitiveHeaders(headers: Record<string, string>): Record<string, string> {
        return Object.fromEntries(
            Object.entries(headers).map(([key, value]) => [key, sensitiveKeys.test(key) ? '[Filtered]' : value]),
        );
    }

    Sentry.init({
        app,
        // SAFETY: the enclosing `if` checked this env var is set, and Vite exposes
        // every env value as a string.
        dsn: import.meta.env['VITE_SENTRY_DSN_PUBLIC'] as string,
        beforeSend(event) {
            // SAFETY: Sentry captures a request body it could serialise, so it is
            // JSON if it is anything; the guard below decides whether it is an object.
            const requestData = event.request?.data as JsonValue | undefined;
            if (event.request && isJsonObject(requestData)) {
                event.request.data = stripSensitiveData(requestData);
            }
            if (event.request?.headers) {
                event.request.headers = stripSensitiveHeaders(event.request.headers);
            }

            // Check if it is an exception, and if so, show the report dialog
            if (event.exception) {
                Sentry.showReportDialog({ eventId: event.event_id });
            }
            return event;
        },
        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
                if (breadcrumb.data) {
                    for (const key of Object.keys(breadcrumb.data)) {
                        if (sensitiveKeys.test(key)) {
                            breadcrumb.data[key] = '[Filtered]';
                        }
                    }
                }
            }
            return breadcrumb;
        },
        integrations: [
            Sentry.browserTracingIntegration({
                router: router,
            }),
            Sentry.vueIntegration({
                tracingOptions: {
                    trackComponents: true,
                },
            }),
        ],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        // SAFETY: Vite exposes env values as strings; Sentry parses this one, and
        // the default covers it being absent.
        tracesSampleRate: (import.meta.env['VITE_SENTRY_TRACES_SAMPLE_RATE'] as number) ?? 0.2,
    });
}
